import React, { useState } from 'react';
import {
  ShieldCheck,
  Building2,
  MailCheck,
  UserCheck,
  KeyRound,
  ShieldAlert,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Lock,
  ChevronRight,
  Sparkles,
  BadgeCheck,
  Award
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

interface NavSection {
  id: string;
  title: string;
  icon: React.ElementType;
}

const sections: NavSection[] = [
  { id: 'overview', title: '1. Building Trust', icon: ShieldCheck },
  { id: 'why-required', title: '2. Why Verification Is Required', icon: Lock },
  { id: 'company-verification', title: '3. Company Verification', icon: Building2 },
  { id: 'email-verification', title: '4. Business Email Verification', icon: MailCheck },
  { id: 'identity-verification', title: '5. Recruiter Identity Verification', icon: UserCheck },
  { id: 'candidate-access', title: '6. Access to Candidate Profiles', icon: KeyRound },
  { id: 'fraud-prevention', title: '7. Fraud Prevention Measures', icon: ShieldAlert },
  { id: 'account-rejection', title: '8. Rejection Conditions', icon: XCircle },
  { id: 'ongoing-compliance', title: '9. Ongoing Compliance', icon: RefreshCw },
  { id: 'account-suspension', title: '10. Suspension & Revocation', icon: AlertTriangle },
  { id: 'future-roadmap', title: '11. Future Trust Roadmap', icon: Award },
];

const rejectionReasons = [
  'False or misleading business information.',
  'Inability to verify corporate registration or domain ownership.',
  'Use of fraudulent or impersonated identities.',
  'Suspicious or predatory recruitment practices.',
  'Violations of platform privacy guidelines.',
  'Repeated submission of inaccurate verification documentation.',
  'Unauthorized representation of an organization.',
  'Activities that compromise candidate privacy or platform security.',
];

const futureFrameworks = [
  { title: 'Verification Badge Program', desc: 'Visual trust indicators for fully vetted enterprise accounts.' },
  { title: 'Enterprise Recruiter Verification', desc: 'Enhanced SSO and multi-seat corporate governance.' },
  { title: 'Agency Recruiter Verification', desc: 'Specialized auditing for third-party executive search firms.' },
  { title: 'Background Screening Standards', desc: 'Optional third-party compliance verification for key hirers.' },
  { title: 'Compliance & Regulatory Checks', desc: 'Automated DPDP and regional data privacy compliance validation.' },
  { title: 'Recruiter Trust Score / Rating', desc: 'Quality ratings based on response velocity and professional engagement.' },
];

export const RecruiterVerificationPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Background Cyber Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                GOVERNANCE & TRUST
              </span>
              <span className="text-xs text-slate-500">Motherson Infotech Division</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              Recruiter Verification Policy
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <ShieldCheck className="w-4 h-4 text-cyan-400" />
            <span>Vetted Recruiter Ecosystem</span>
          </div>
        </div>
      </header>

      {/* Main Container Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Policy Navigation
            </h2>
            <nav className="space-y-1">
              {sections.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Area */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-10">
          
          {/* Section 1: Building a Trusted Hiring Environment */}
          <section id="overview" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">1. Building a Trusted Hiring Environment</h2>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm">
              Nexgent is committed to maintaining a professional, secure, and highly curated leadership hiring platform. To protect candidate privacy and preserve platform integrity, all recruiters and hiring organizations may be subject to a strict verification process before gaining access to candidate profiles and recruitment features.
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              Verification minimizes fraudulent activity, protects confidential professional details, and establishes a trusted environment for meaningful executive hires.
            </p>
          </section>

          {/* Section 2: Why Verification Is Required */}
          <section id="why-required" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Lock className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">2. Why Verification Is Required</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Recruiter verification fulfills several core security functions across the ecosystem:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-1">
              <div className="p-3 bg-slate-950/70 border border-slate-800/80 rounded-lg text-slate-300 flex items-start gap-2">
                <BadgeCheck className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span>Protect candidate privacy and prevent unauthorized profile scraping.</span>
              </div>
              <div className="p-3 bg-slate-950/70 border border-slate-800/80 rounded-lg text-slate-300 flex items-start gap-2">
                <BadgeCheck className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span>Reduce fraudulent, misleading, or impersonated accounts.</span>
              </div>
              <div className="p-3 bg-slate-950/70 border border-slate-800/80 rounded-lg text-slate-300 flex items-start gap-2">
                <BadgeCheck className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span>Verify legitimate hiring organizations and business entities.</span>
              </div>
              <div className="p-3 bg-slate-950/70 border border-slate-800/80 rounded-lg text-slate-300 flex items-start gap-2">
                <BadgeCheck className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span>Support responsible, high-intent executive recruitment practices.</span>
              </div>
            </div>
            <p className="text-slate-500 text-[11px] font-mono pt-1">
              * Note: Verification validates entity legitimacy and professional authorization but does not guarantee specific hiring outcomes.
            </p>
          </section>

          {/* Section 3: Company Verification */}
          <section id="company-verification" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Building2 className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">3. Company Verification</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Organizations seeking access to Nexgent must provide information that confirms their operational identity and hiring authorization. Required data points may include:
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-400">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Registered Business Name & Website URL</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Physical Corporate Address</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Industry Classification & Operations</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Corporate Registration (CIN/GST/Tax IDs)</span>
                </div>
              </div>
            </div>
            <p className="text-slate-400 text-xs">
              Nexgent reserves the right to review publicly available business records or request supporting corporate documentation whenever necessary.
            </p>
          </section>

          {/* Section 4: Business Email Verification */}
          <section id="email-verification" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <MailCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">4. Business Email Verification</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Recruiters are expected to register using a valid corporate business email address associated with their organization domain.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs font-mono">
              <span className="text-cyan-400 font-semibold uppercase">Accepted Address Formats:</span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-slate-300 pt-1">
                <div className="bg-slate-900 p-2 rounded border border-slate-800 text-center">recruiter@company.com</div>
                <div className="bg-slate-900 p-2 rounded border border-slate-800 text-center">hiring@company.com</div>
                <div className="bg-slate-900 p-2 rounded border border-slate-800 text-center">talent@company.com</div>
              </div>
            </div>
            <p className="text-slate-400 text-xs leading-relaxed">
              Personal webmail accounts (e.g., Gmail, Yahoo, Outlook) are subject to manual compliance reviews and may require additional proof of organizational authorization.
            </p>
          </section>

          {/* Section 5: Recruiter Identity Verification */}
          <section id="identity-verification" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <UserCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">5. Recruiter Identity Verification</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              To ensure individual recruiters are genuinely authorized to act on behalf of their organization, Nexgent may employ multiple verification mechanisms:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Domain OTP & Phone Validation</div>
                <div className="text-slate-400">Multi-factor domain matching and corporate contact number verification.</div>
              </div>
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Professional Profile Audits</div>
                <div className="text-slate-400">Review of verified professional credentials and corporate leadership history.</div>
              </div>
            </div>
          </section>

          {/* Section 6: Access to Candidate Profiles */}
          <section id="candidate-access" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <KeyRound className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">6. Access to Candidate Profiles</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Verification status dictates platform permissions. Fully verified recruiters receive access to:
            </p>
            <ul className="text-xs text-slate-400 space-y-2 list-disc pl-5">
              <li>Advanced executive talent search filters and experience metrics.</li>
              <li>Anonymized professional profile cards and leadership summaries.</li>
              <li>Direct invitation-based outreach tools utilizing monthly plan credits.</li>
              <li>Unlocked contact information upon candidate invitation acceptance.</li>
            </ul>
            <div className="p-3 bg-amber-950/30 border border-amber-900/40 text-amber-300 rounded-lg text-xs">
              Nexgent reserves the right to restrict search capabilities until verification procedures are fully satisfied.
            </div>
          </section>

          {/* Section 7: Fraud Prevention Measures */}
          <section id="fraud-prevention" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldAlert className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">7. Fraud Prevention Measures</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              We employ continuous security and monitoring mechanisms to safeguard network participants:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Automated Activity Monitoring</div>
                <div className="text-slate-400">Detection of abnormal search patterns, bulk profile views, or automated scraping tools.</div>
              </div>
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Duplicate Account Detection</div>
                <div className="text-slate-400">Cross-referencing corporate domain records to block unauthorized multi-account creation.</div>
              </div>
            </div>
          </section>

          {/* Section 8: Reasons a Recruiter Account May Be Rejected */}
          <section id="account-rejection" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <XCircle className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">8. Reasons a Recruiter Account May Be Rejected</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Nexgent reserves the right to decline recruiter registration or restrict platform access based on risk, compliance, and candidate safety considerations:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs pt-1">
              {rejectionReasons.map((reason, idx) => (
                <div key={idx} className="p-3 bg-slate-950/80 border border-slate-800/80 rounded-xl flex items-start gap-2 text-slate-300">
                  <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  <span>{reason}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Section 9: Ongoing Verification and Compliance */}
          <section id="ongoing-compliance" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <RefreshCw className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">9. Ongoing Verification and Compliance</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Verification is not a static process. Nexgent periodically audits active recruiter accounts and may request updated corporate documentation or domain re-validation.
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              Recruiters are responsible for keeping company records current. Failure to respond to compliance updates may lead to temporary permission suspensions.
            </p>
          </section>

          {/* Section 10: Account Suspension or Revocation */}
          <section id="account-suspension" className="scroll-mt-28 space-y-4 bg-red-950/10 border border-red-900/30 p-6 md:p-8 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-950/60 text-red-400 rounded-lg border border-red-900/50">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">10. Account Suspension or Revocation</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Nexgent reserves the right to suspend, restrict, or permanently revoke recruiter access with or without prior notice if:
            </p>
            <ul className="text-xs text-slate-300 space-y-1.5 list-disc pl-5">
              <li>Verification requirements remain unsatisfied past the initial grace period.</li>
              <li>Fraudulent, abusive, or spam outreach behavior is detected.</li>
              <li>Candidate privacy bounds or platform security protocols are breached.</li>
            </ul>
          </section>

          {/* Section 11: Future Roadmap */}
          <section id="future-roadmap" className="scroll-mt-28 space-y-6 bg-slate-900/80 p-6 md:p-8 rounded-2xl border border-cyan-800/50 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">11. Future Trust Framework Roadmap</h2>
                <p className="text-xs text-slate-400">Upcoming security enhancements for enterprise corporate buyers</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
              {futureFrameworks.map((item, idx) => (
                <div key={idx} className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-1">
                  <div className="text-cyan-400 font-semibold text-xs flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>{item.title}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-cyan-500 font-mono mt-4">
              {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent Governance. All rights reserved. */}
              Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First
            </div>
          </section>

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default RecruiterVerificationPolicy;